// ===== K-Tec Catalog Store — Multi-Brand Product Management =====
// Zustand store with localStorage persistence.
// Switch USE_API=true to route through Laravel REST API (no UI changes needed).

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Brand, Category, CatalogProduct, CCTVPackage, PackageItem, ProductImage } from '../types/catalog';

// ─── Seed Brands ─────────────────────────────────────────────────────────────
const seedBrands: Brand[] = [
  { id: 'b1', name: 'Dahua', slug: 'dahua', description: 'Global leading solution provider in the security industry.', country: 'China', isActive: true, displayOrder: 1, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'b2', name: 'Hikvision', slug: 'hikvision', description: 'World\'s leading video surveillance manufacturer.', country: 'China', isActive: true, displayOrder: 2, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'b3', name: 'EZVIZ', slug: 'ezviz', description: 'Intelligent consumer security brand by Hikvision.', country: 'China', isActive: true, displayOrder: 3, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'b4', name: 'IMOU', slug: 'imou', description: 'Smart home and business security solutions by Dahua.', country: 'China', isActive: true, displayOrder: 4, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'b5', name: 'Other / Generic', slug: 'other', description: 'Generic and third-party compatible accessories.', isActive: true, displayOrder: 5, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
];

// ─── Seed Categories ──────────────────────────────────────────────────────────
const seedCategories: Category[] = [
  // Top-level
  { id: 'cat1', name: 'CCTV & Video Surveillance', slug: 'cctv', isActive: true, displayOrder: 1, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'cat2', name: 'Smart Security', slug: 'smart-security', isActive: true, displayOrder: 2, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'cat3', name: 'Networking', slug: 'networking', isActive: true, displayOrder: 3, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'cat4', name: 'Installation Accessories', slug: 'accessories', isActive: true, displayOrder: 4, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  // CCTV Subcategories
  { id: 'sub1', name: 'Analog HD Cameras', slug: 'analog-hd', parentId: 'cat1', isActive: true, displayOrder: 1, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub2', name: 'IP Cameras', slug: 'ip-cameras', parentId: 'cat1', isActive: true, displayOrder: 2, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub3', name: 'Wi-Fi Cameras', slug: 'wifi-cameras', parentId: 'cat1', isActive: true, displayOrder: 3, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub4', name: '4G SIM Cameras', slug: '4g-cameras', parentId: 'cat1', isActive: true, displayOrder: 4, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub5', name: 'PTZ Cameras', slug: 'ptz-cameras', parentId: 'cat1', isActive: true, displayOrder: 5, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub6', name: 'Indoor Cameras', slug: 'indoor', parentId: 'cat1', isActive: true, displayOrder: 6, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub7', name: 'Outdoor Cameras', slug: 'outdoor', parentId: 'cat1', isActive: true, displayOrder: 7, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub8', name: 'Bullet Cameras', slug: 'bullet', parentId: 'cat1', isActive: true, displayOrder: 8, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub9', name: 'Dome Cameras', slug: 'dome', parentId: 'cat1', isActive: true, displayOrder: 9, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub10', name: 'Turret Cameras', slug: 'turret', parentId: 'cat1', isActive: true, displayOrder: 10, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub11', name: 'Color Night Vision Cameras', slug: 'color-night', parentId: 'cat1', isActive: true, displayOrder: 11, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub12', name: 'Smart AI Cameras', slug: 'smart-ai', parentId: 'cat1', isActive: true, displayOrder: 12, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub13', name: 'NVR Recorders', slug: 'nvr', parentId: 'cat1', isActive: true, displayOrder: 13, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub14', name: 'DVR / XVR Recorders', slug: 'dvr', parentId: 'cat1', isActive: true, displayOrder: 14, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub15', name: 'Hard Drives / Surveillance Storage', slug: 'storage', parentId: 'cat1', isActive: true, displayOrder: 15, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  // Smart Security Subcategories
  { id: 'sub16', name: 'Video Doorbells', slug: 'doorbells', parentId: 'cat2', isActive: true, displayOrder: 1, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub17', name: 'Smart Door Locks', slug: 'door-locks', parentId: 'cat2', isActive: true, displayOrder: 2, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub18', name: 'Alarm Systems', slug: 'alarms', parentId: 'cat2', isActive: true, displayOrder: 3, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub19', name: 'PIR Sensors', slug: 'pir', parentId: 'cat2', isActive: true, displayOrder: 4, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub20', name: 'Door / Window Sensors', slug: 'door-sensors', parentId: 'cat2', isActive: true, displayOrder: 5, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub21', name: 'Sirens', slug: 'sirens', parentId: 'cat2', isActive: true, displayOrder: 6, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub22', name: 'Smart Security Hubs', slug: 'hubs', parentId: 'cat2', isActive: true, displayOrder: 7, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  // Networking Subcategories
  { id: 'sub23', name: 'PoE Switches', slug: 'poe-switches', parentId: 'cat3', isActive: true, displayOrder: 1, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub24', name: 'Network Switches', slug: 'switches', parentId: 'cat3', isActive: true, displayOrder: 2, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub25', name: 'Wi-Fi Routers', slug: 'routers', parentId: 'cat3', isActive: true, displayOrder: 3, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub26', name: 'Access Points', slug: 'access-points', parentId: 'cat3', isActive: true, displayOrder: 4, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub27', name: 'Outdoor CPE', slug: 'outdoor-cpe', parentId: 'cat3', isActive: true, displayOrder: 5, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub28', name: 'Network Accessories', slug: 'net-accessories', parentId: 'cat3', isActive: true, displayOrder: 6, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  // Installation Accessories Subcategories
  { id: 'sub29', name: 'CCTV Cables', slug: 'cctv-cables', parentId: 'cat4', isActive: true, displayOrder: 1, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub30', name: 'CAT6 Cables', slug: 'cat6', parentId: 'cat4', isActive: true, displayOrder: 2, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub31', name: 'Power Supplies', slug: 'power-supplies', parentId: 'cat4', isActive: true, displayOrder: 3, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub32', name: 'Connectors', slug: 'connectors', parentId: 'cat4', isActive: true, displayOrder: 4, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub33', name: 'Junction Boxes', slug: 'junction-boxes', parentId: 'cat4', isActive: true, displayOrder: 5, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub34', name: 'Camera Brackets', slug: 'brackets', parentId: 'cat4', isActive: true, displayOrder: 6, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub35', name: 'Racks', slug: 'racks', parentId: 'cat4', isActive: true, displayOrder: 7, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub36', name: 'UPS / Backup Power', slug: 'ups', parentId: 'cat4', isActive: true, displayOrder: 8, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
  { id: 'sub37', name: 'Installation Accessories', slug: 'install-acc', parentId: 'cat4', isActive: true, displayOrder: 9, createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z' },
];

// ─── Seed Catalog Products ────────────────────────────────────────────────────
const seedCatalogProducts: CatalogProduct[] = [
  // ── Dahua IP Cameras ──
  {
    id: 'cp-dah-001', name: 'Dahua IPC-HDW2849H-S-IL 8MP Smart Dual Light Camera',
    brandId: 'b1', modelNumber: 'IPC-HDW2849H-S-IL', sku: 'DAH-8MP-SDL-001',
    categoryId: 'cat1', subcategoryId: 'sub2', productType: 'Dome Camera',
    shortDescription: '8MP Smart Dual Light Fixed-focal Eyeball Network Camera with AI features',
    mainImage: '', galleryImages: [],
    regularPrice: 28500, salePrice: 26500, costPrice: 18000, installationPrice: 2500,
    stock: 25, reservedStock: 3, minStockAlert: 5, inStock: true,
    isFeatured: true, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '3 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '8MP (3840×2160)' }],
    resolution: '8MP (4K)', megapixels: 8, lensSize: '2.8mm', nightVisionDistance: '30m',
    lightingType: 'Smart Dual Light', usage: 'Both', ipRating: 'IP67',
    hasAudio: true, hasTwoWayAudio: false, hasAIHumanDetection: true,
    hasVehicleDetection: true, hasMotionDetection: true, hasPOE: true,
    powerRequirements: 'PoE / DC12V', mobileAppSupport: 'DMSS',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-dah-002', name: 'Dahua IPC-HFW2849S-S-IL 8MP Smart Dual Light Bullet',
    brandId: 'b1', modelNumber: 'IPC-HFW2849S-S-IL', sku: 'DAH-8MP-BUL-001',
    categoryId: 'cat1', subcategoryId: 'sub8', productType: 'Bullet Camera',
    shortDescription: '8MP Smart Dual Light Fixed-focal Bullet Network Camera',
    mainImage: '', galleryImages: [],
    regularPrice: 29500, salePrice: 27500, costPrice: 19000,
    stock: 20, reservedStock: 2, minStockAlert: 5, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '3 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '8MP (3840×2160)' }],
    resolution: '8MP (4K)', megapixels: 8, lensSize: '2.8mm / 3.6mm', nightVisionDistance: '60m',
    lightingType: 'Smart Dual Light', usage: 'Outdoor', ipRating: 'IP67',
    hasAudio: true, hasAIHumanDetection: true, hasVehicleDetection: true, hasPOE: true,
    powerRequirements: 'PoE / DC12V', mobileAppSupport: 'DMSS',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-dah-003', name: 'Dahua IPC-HDW3849HP-AS-PV 8MP TiOC Active Deterrence',
    brandId: 'b1', modelNumber: 'IPC-HDW3849HP-AS-PV', sku: 'DAH-8MP-TIOC-001',
    categoryId: 'cat1', subcategoryId: 'sub2', productType: 'Smart AI Camera',
    shortDescription: '8MP TiOC Full-color Active Deterrence Eyeball Camera',
    mainImage: '', galleryImages: [],
    regularPrice: 52000, salePrice: 48000, costPrice: 33000,
    stock: 18, reservedStock: 4, minStockAlert: 3, inStock: true,
    isFeatured: true, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '3 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '8MP (3840×2160)' }],
    resolution: '8MP (4K)', megapixels: 8, lensSize: '2.8mm', nightVisionDistance: '40m',
    lightingType: 'Full Color', usage: 'Both', ipRating: 'IP67',
    hasAudio: true, hasTwoWayAudio: true, hasAIHumanDetection: true, hasVehicleDetection: true, hasPOE: true,
    powerRequirements: 'PoE / DC12V', mobileAppSupport: 'DMSS',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-dah-004', name: 'Dahua HAC-HDW1500TLP-A-LED 5MP Full-Color HDCVI Dome',
    brandId: 'b1', modelNumber: 'HAC-HDW1500TLP-A-LED', sku: 'DAH-5MP-FC-001',
    categoryId: 'cat1', subcategoryId: 'sub1', productType: 'Analog HD Dome',
    shortDescription: '5MP Full-Color HDCVI Fixed-focal Eyeball Camera',
    mainImage: '', galleryImages: [],
    regularPrice: 19500, salePrice: 18000, costPrice: 12000,
    stock: 60, reservedStock: 8, minStockAlert: 10, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '3 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '5MP' }],
    resolution: '5MP', megapixels: 5, lensSize: '2.8mm', nightVisionDistance: '30m',
    lightingType: 'Full Color', usage: 'Both', ipRating: 'IP67',
    hasAudio: true, powerRequirements: 'DC12V / CVI Power', mobileAppSupport: 'DMSS',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-dah-005', name: 'Dahua XVR5108HS-I3 8CH WizSense DVR',
    brandId: 'b1', modelNumber: 'XVR5108HS-I3', sku: 'DAH-DVR-8CH-001',
    categoryId: 'cat1', subcategoryId: 'sub14', productType: 'DVR Recorder',
    shortDescription: '8 Channel Penta-brid 5M-N/1080P WizSense DVR',
    mainImage: '', galleryImages: [],
    regularPrice: 38500, salePrice: 35000, costPrice: 24000,
    stock: 20, reservedStock: 3, minStockAlert: 5, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'channels', label: 'Channels', value: '8' }],
    usage: 'Indoor', hasAIHumanDetection: true, hasVehicleDetection: true,
    powerRequirements: 'DC12V 2A', mobileAppSupport: 'DMSS',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-dah-006', name: 'Dahua NVR4108HS-8P-AI/ANZ 8CH NVR with PoE',
    brandId: 'b1', modelNumber: 'NVR4108HS-8P-AI/ANZ', sku: 'DAH-NVR-8CH-001',
    categoryId: 'cat1', subcategoryId: 'sub13', productType: 'NVR Recorder',
    shortDescription: '8 Channel 1U 8PoE WizMind Network Video Recorder',
    mainImage: '', galleryImages: [],
    regularPrice: 52000, salePrice: 48500, costPrice: 34000,
    stock: 15, reservedStock: 2, minStockAlert: 3, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: false, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'channels', label: 'Channels', value: '8' }],
    usage: 'Indoor', hasPOE: true, powerRequirements: 'DC48V', mobileAppSupport: 'DMSS',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  // ── Hikvision Cameras ──
  {
    id: 'cp-hik-001', name: 'Hikvision DS-2CD2T47G2-LSU/SL 4MP ColorVu Bullet',
    brandId: 'b2', modelNumber: 'DS-2CD2T47G2-LSU/SL', sku: 'HIK-4MP-CV-BUL-001',
    categoryId: 'cat1', subcategoryId: 'sub8', productType: 'Bullet Camera',
    shortDescription: '4MP ColorVu Fixed Turret Network Camera with Strobe Light and Audio Alarm',
    mainImage: '', galleryImages: [],
    regularPrice: 35000, salePrice: 32000, costPrice: 22000,
    stock: 30, reservedStock: 5, minStockAlert: 5, inStock: true,
    isFeatured: true, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '3 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '4MP' }],
    resolution: '4MP', megapixels: 4, lensSize: '2.8mm / 4mm', nightVisionDistance: '60m',
    lightingType: 'Full Color', usage: 'Outdoor', ipRating: 'IP67',
    hasAudio: true, hasTwoWayAudio: true, hasAIHumanDetection: true, hasPOE: true,
    powerRequirements: 'PoE / DC12V', mobileAppSupport: 'Hik-Connect',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-hik-002', name: 'Hikvision DS-2CD2347G2-LU 4MP ColorVu Dome Camera',
    brandId: 'b2', modelNumber: 'DS-2CD2347G2-LU', sku: 'HIK-4MP-CV-DOM-001',
    categoryId: 'cat1', subcategoryId: 'sub9', productType: 'Dome Camera',
    shortDescription: '4MP ColorVu Fixed Dome Network Camera',
    mainImage: '', galleryImages: [],
    regularPrice: 34000, salePrice: 31000, costPrice: 21000,
    stock: 35, reservedStock: 6, minStockAlert: 5, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '3 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '4MP' }],
    resolution: '4MP', megapixels: 4, lensSize: '2.8mm', nightVisionDistance: '40m',
    lightingType: 'Full Color', usage: 'Both', ipRating: 'IP67',
    hasAudio: false, hasAIHumanDetection: true, hasPOE: true,
    powerRequirements: 'PoE / DC12V', mobileAppSupport: 'Hik-Connect',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-hik-003', name: 'Hikvision DS-2CD2T47G2-L 4MP ColorVu Strobe Camera',
    brandId: 'b2', modelNumber: 'DS-2CD2T47G2-L', sku: 'HIK-4MP-STROBE-001',
    categoryId: 'cat1', subcategoryId: 'sub11', productType: 'Color Night Vision',
    shortDescription: '4MP ColorVu Strobe Light and Audio Alarm Fixed Bullet Network Camera',
    mainImage: '', galleryImages: [],
    regularPrice: 36000, salePrice: 33000, costPrice: 23000,
    stock: 20, reservedStock: 3, minStockAlert: 5, inStock: true,
    isFeatured: false, isNewProduct: true, isBestSeller: false, status: 'active',
    warrantyPeriod: '3 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '4MP' }],
    resolution: '4MP', megapixels: 4, lensSize: '2.8mm / 4mm', nightVisionDistance: '60m',
    lightingType: 'Full Color', usage: 'Outdoor', ipRating: 'IP67',
    hasAudio: true, hasTwoWayAudio: true, hasAIHumanDetection: true, hasPOE: true,
    powerRequirements: 'PoE / DC12V', mobileAppSupport: 'Hik-Connect',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-hik-004', name: 'Hikvision DS-7608NI-K2/8P 8CH 4K NVR',
    brandId: 'b2', modelNumber: 'DS-7608NI-K2/8P', sku: 'HIK-NVR-8CH-001',
    categoryId: 'cat1', subcategoryId: 'sub13', productType: 'NVR Recorder',
    shortDescription: '8 Channel 4K NVR with 8 PoE ports and H.265+',
    mainImage: '', galleryImages: [],
    regularPrice: 68000, salePrice: 65000, costPrice: 45000,
    stock: 12, reservedStock: 3, minStockAlert: 3, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'channels', label: 'Channels', value: '8' }],
    usage: 'Indoor', hasPOE: true, powerRequirements: 'DC48V', mobileAppSupport: 'Hik-Connect',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-hik-005', name: 'Hikvision DS-7616NI-K2/16P 16CH 4K NVR',
    brandId: 'b2', modelNumber: 'DS-7616NI-K2/16P', sku: 'HIK-NVR-16CH-001',
    categoryId: 'cat1', subcategoryId: 'sub13', productType: 'NVR Recorder',
    shortDescription: '16 Channel 4K NVR with 16 PoE ports',
    mainImage: '', galleryImages: [],
    regularPrice: 105000, salePrice: 98000, costPrice: 68000,
    stock: 8, reservedStock: 2, minStockAlert: 3, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: false, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'channels', label: 'Channels', value: '16' }],
    usage: 'Indoor', hasPOE: true, powerRequirements: 'DC48V', mobileAppSupport: 'Hik-Connect',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-hik-006', name: 'Hikvision DS-2CD2T43G2-4I 4MP AcuSense Bullet',
    brandId: 'b2', modelNumber: 'DS-2CD2T43G2-4I', sku: 'HIK-4MP-ACUSENSE-001',
    categoryId: 'cat1', subcategoryId: 'sub8', productType: 'Bullet Camera',
    shortDescription: '4MP AcuSense Fixed Turret Network Camera with IR',
    mainImage: '', galleryImages: [],
    regularPrice: 30000, salePrice: 27500, costPrice: 19000,
    stock: 40, reservedStock: 5, minStockAlert: 10, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '3 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '4MP' }],
    resolution: '4MP', megapixels: 4, lensSize: '2.8mm / 4mm / 6mm', nightVisionDistance: '80m',
    lightingType: 'IR', usage: 'Outdoor', ipRating: 'IP67',
    hasAIHumanDetection: true, hasVehicleDetection: true, hasPOE: true,
    powerRequirements: 'PoE / DC12V', mobileAppSupport: 'Hik-Connect',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  // ── EZVIZ Cameras ──
  {
    id: 'cp-ezv-001', name: 'EZVIZ H8c 4MP 360° Pan/Tilt Outdoor Camera',
    brandId: 'b3', modelNumber: 'CS-H8c', sku: 'EZV-H8C-4MP-001',
    categoryId: 'cat1', subcategoryId: 'sub3', productType: 'Wi-Fi PTZ Camera',
    shortDescription: '4MP 360° outdoor Wi-Fi pan/tilt camera with color night vision',
    mainImage: '', galleryImages: [],
    regularPrice: 22000, salePrice: 19500, costPrice: 13500,
    stock: 30, reservedStock: 5, minStockAlert: 5, inStock: true,
    isFeatured: true, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '4MP' }],
    resolution: '4MP', megapixels: 4, lensSize: '4mm (motorized)', nightVisionDistance: '30m',
    lightingType: 'Full Color', usage: 'Outdoor', ipRating: 'IP67',
    hasAudio: true, hasTwoWayAudio: true, hasWiFi: true, hasAIHumanDetection: true,
    hasMotionDetection: true, powerRequirements: 'DC12V',
    mobileAppSupport: 'EZVIZ App', storageSupport: 'NVR / MicroSD',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-ezv-002', name: 'EZVIZ C6N 2MP 360° Indoor Pan/Tilt Camera',
    brandId: 'b3', modelNumber: 'CS-C6N', sku: 'EZV-C6N-2MP-001',
    categoryId: 'cat1', subcategoryId: 'sub3', productType: 'Wi-Fi Indoor Camera',
    shortDescription: '2MP 360° indoor Wi-Fi pan/tilt camera, ideal for baby monitoring',
    mainImage: '', galleryImages: [],
    regularPrice: 12500, salePrice: 11000, costPrice: 7500,
    stock: 50, reservedStock: 8, minStockAlert: 10, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '2MP (1080P)' }],
    resolution: '2MP (1080P)', megapixels: 2, nightVisionDistance: '10m',
    lightingType: 'IR', usage: 'Indoor',
    hasAudio: true, hasTwoWayAudio: true, hasWiFi: true, hasAIHumanDetection: true,
    powerRequirements: 'DC5V', mobileAppSupport: 'EZVIZ App', storageSupport: 'MicroSD / Cloud',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-ezv-003', name: 'EZVIZ DB1C Wi-Fi Video Doorbell',
    brandId: 'b3', modelNumber: 'CS-DB1C', sku: 'EZV-DB1C-001',
    categoryId: 'cat2', subcategoryId: 'sub16', productType: 'Video Doorbell',
    shortDescription: 'Wi-Fi Video Doorbell with 2K resolution and AI detection',
    mainImage: '', galleryImages: [],
    regularPrice: 16500, salePrice: 15000, costPrice: 10000,
    stock: 20, reservedStock: 3, minStockAlert: 5, inStock: true,
    isFeatured: false, isNewProduct: true, isBestSeller: false, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '2K (2560×1920)' }],
    resolution: '2K (2560×1920)', hasAudio: true, hasTwoWayAudio: true, hasWiFi: true,
    hasAIHumanDetection: true, powerRequirements: 'Battery / Wired',
    mobileAppSupport: 'EZVIZ App',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-ezv-004', name: 'EZVIZ C8W Pro 4MP 4G Outdoor Wi-Fi Bullet Camera',
    brandId: 'b3', modelNumber: 'CS-C8W-Pro', sku: 'EZV-C8W-PRO-001',
    categoryId: 'cat1', subcategoryId: 'sub4', productType: '4G SIM Camera',
    shortDescription: '4MP outdoor color night vision Wi-Fi & 4G bullet camera',
    mainImage: '', galleryImages: [],
    regularPrice: 28000, salePrice: 25500, costPrice: 17000,
    stock: 15, reservedStock: 2, minStockAlert: 3, inStock: true,
    isFeatured: false, isNewProduct: true, isBestSeller: false, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '4MP' }],
    resolution: '4MP', megapixels: 4, lightingType: 'Full Color', usage: 'Outdoor', ipRating: 'IP67',
    hasAudio: true, hasTwoWayAudio: true, hasWiFi: true, hasAIHumanDetection: true,
    powerRequirements: 'DC12V', mobileAppSupport: 'EZVIZ App', storageSupport: 'MicroSD / NVR',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  // ── IMOU Cameras ──
  {
    id: 'cp-imu-001', name: 'IMOU Bullet 2E 4MP Wi-Fi Outdoor Camera',
    brandId: 'b4', modelNumber: 'IPC-F42FEP-D', sku: 'IMOU-4MP-BUL-001',
    categoryId: 'cat1', subcategoryId: 'sub3', productType: 'Wi-Fi Bullet Camera',
    shortDescription: '4MP Built-in Mic Wi-Fi Outdoor Camera with Smart Detection',
    mainImage: '', galleryImages: [],
    regularPrice: 18500, salePrice: 16500, costPrice: 11000,
    stock: 35, reservedStock: 5, minStockAlert: 8, inStock: true,
    isFeatured: true, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '4MP' }],
    resolution: '4MP', megapixels: 4, nightVisionDistance: '30m',
    lightingType: 'IR', usage: 'Outdoor', ipRating: 'IP67',
    hasAudio: true, hasWiFi: true, hasAIHumanDetection: true, hasMotionDetection: true,
    powerRequirements: 'DC12V', mobileAppSupport: 'IMOU Life', storageSupport: 'NVR / MicroSD',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-imu-002', name: 'IMOU Cell Go 4MP 4G Battery-Powered Camera',
    brandId: 'b4', modelNumber: 'IPC-B46LP-D', sku: 'IMOU-4G-BAT-001',
    categoryId: 'cat1', subcategoryId: 'sub4', productType: '4G Battery Camera',
    shortDescription: '4MP 4G battery-powered outdoor camera with solar panel support',
    mainImage: '', galleryImages: [],
    regularPrice: 42000, salePrice: 38500, costPrice: 27000,
    stock: 10, reservedStock: 2, minStockAlert: 3, inStock: true,
    isFeatured: false, isNewProduct: true, isBestSeller: false, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '4MP' }],
    resolution: '4MP', megapixels: 4, lightingType: 'Smart Dual Light', usage: 'Outdoor', ipRating: 'IP67',
    hasAudio: true, hasTwoWayAudio: true, hasWiFi: true, hasAIHumanDetection: true,
    powerRequirements: 'Battery / Solar', mobileAppSupport: 'IMOU Life', storageSupport: 'MicroSD / Cloud',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-imu-003', name: 'IMOU Ranger 2C 2MP Indoor Wi-Fi Pan/Tilt Camera',
    brandId: 'b4', modelNumber: 'IPC-TA22CP', sku: 'IMOU-2MP-PTZ-001',
    categoryId: 'cat1', subcategoryId: 'sub3', productType: 'Wi-Fi Indoor Camera',
    shortDescription: '2MP Indoor Pan/Tilt Auto-tracking Wi-Fi Camera',
    mainImage: '', galleryImages: [],
    regularPrice: 9500, salePrice: 8500, costPrice: 5800,
    stock: 60, reservedStock: 10, minStockAlert: 10, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'resolution', label: 'Resolution', value: '2MP (1080P)' }],
    resolution: '2MP (1080P)', megapixels: 2, nightVisionDistance: '10m',
    lightingType: 'IR', usage: 'Indoor',
    hasAudio: true, hasTwoWayAudio: true, hasWiFi: true, hasSmartTracking: true,
    hasAIHumanDetection: true, powerRequirements: 'DC5V',
    mobileAppSupport: 'IMOU Life', storageSupport: 'MicroSD / Cloud',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  // ── Storage ──
  {
    id: 'cp-sto-001', name: 'Western Digital Purple 1TB Surveillance HDD',
    brandId: 'b5', modelNumber: 'WD10PURZ', sku: 'WD-PURP-1TB-001',
    categoryId: 'cat1', subcategoryId: 'sub15', productType: 'Surveillance HDD',
    shortDescription: '1TB WD Purple Surveillance-class Hard Drive for CCTV recorders',
    mainImage: '', galleryImages: [],
    regularPrice: 16500, salePrice: 15000, costPrice: 10500,
    stock: 40, reservedStock: 5, minStockAlert: 10, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '3 Years',
    specs: [{ key: 'capacity', label: 'Capacity', value: '1TB' }],
    usage: 'Indoor', powerRequirements: 'SATA',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-sto-002', name: 'Western Digital Purple 2TB Surveillance HDD',
    brandId: 'b5', modelNumber: 'WD20PURZ', sku: 'WD-PURP-2TB-001',
    categoryId: 'cat1', subcategoryId: 'sub15', productType: 'Surveillance HDD',
    shortDescription: '2TB WD Purple Surveillance-class Hard Drive',
    mainImage: '', galleryImages: [],
    regularPrice: 24500, salePrice: 22500, costPrice: 16000,
    stock: 35, reservedStock: 5, minStockAlert: 8, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '3 Years',
    specs: [{ key: 'capacity', label: 'Capacity', value: '2TB' }],
    usage: 'Indoor', powerRequirements: 'SATA',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-sto-003', name: 'Seagate SkyHawk 4TB Surveillance HDD',
    brandId: 'b5', modelNumber: 'ST4000VX016', sku: 'SEA-SKYHAWK-4TB-001',
    categoryId: 'cat1', subcategoryId: 'sub15', productType: 'Surveillance HDD',
    shortDescription: '4TB Seagate SkyHawk Surveillance HDD, designed for DVR/NVR',
    mainImage: '', galleryImages: [],
    regularPrice: 38500, salePrice: 35000, costPrice: 25000,
    stock: 20, reservedStock: 4, minStockAlert: 5, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: false, status: 'active',
    warrantyPeriod: '3 Years',
    specs: [{ key: 'capacity', label: 'Capacity', value: '4TB' }],
    usage: 'Indoor', powerRequirements: 'SATA',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  // ── Networking ──
  {
    id: 'cp-net-001', name: 'Dahua PFS3008-8GT-96 8-Port PoE Switch',
    brandId: 'b1', modelNumber: 'PFS3008-8GT-96', sku: 'DAH-POE-8P-001',
    categoryId: 'cat3', subcategoryId: 'sub23', productType: 'PoE Switch',
    shortDescription: '8-port Gigabit PoE switch, 96W total power budget',
    mainImage: '', galleryImages: [],
    regularPrice: 18500, salePrice: 16500, costPrice: 11000,
    stock: 30, reservedStock: 4, minStockAlert: 5, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'ports', label: 'PoE Ports', value: '8' }],
    usage: 'Indoor', hasPOE: true, powerRequirements: 'AC100~240V',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-net-002', name: 'Hikvision DS-3E0108P-E/M 8-Port Fast Ethernet PoE Switch',
    brandId: 'b2', modelNumber: 'DS-3E0108P-E/M', sku: 'HIK-POE-8P-001',
    categoryId: 'cat3', subcategoryId: 'sub23', productType: 'PoE Switch',
    shortDescription: '8-port 10/100 Mbps PoE switch, 58W budget, plug-and-play',
    mainImage: '', galleryImages: [],
    regularPrice: 15500, salePrice: 14000, costPrice: 9500,
    stock: 25, reservedStock: 4, minStockAlert: 5, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'ports', label: 'PoE Ports', value: '8' }],
    usage: 'Indoor', hasPOE: true, powerRequirements: 'DC52V',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  // ── Alarm ──
  {
    id: 'cp-alm-001', name: 'Dahua DHI-ARC3000H-GW2(868) Smart Wireless Alarm Kit',
    brandId: 'b1', modelNumber: 'DHI-ARC3000H-GW2', sku: 'DAH-ALM-WIFI-001',
    categoryId: 'cat2', subcategoryId: 'sub18', productType: 'Alarm System',
    shortDescription: 'Smart wireless alarm hub with Wi-Fi/GSM connectivity',
    mainImage: '', galleryImages: [],
    regularPrice: 38000, salePrice: 35000, costPrice: 24000,
    stock: 15, reservedStock: 2, minStockAlert: 3, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: false, status: 'active',
    warrantyPeriod: '2 Years',
    specs: [{ key: 'zones', label: 'Zones', value: 'Up to 64' }],
    usage: 'Indoor', hasWiFi: true, powerRequirements: 'DC12V', mobileAppSupport: 'DMSS',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  // ── Accessories ──
  {
    id: 'cp-acc-001', name: 'CCTV BNC + Power Cable 50m Roll',
    brandId: 'b5', modelNumber: 'BNC-PWR-50M', sku: 'ACC-BNC-50M-001',
    categoryId: 'cat4', subcategoryId: 'sub29', productType: 'CCTV Cable',
    shortDescription: 'Pre-made CCTV BNC video + power cable combo, 50m roll',
    mainImage: '', galleryImages: [],
    regularPrice: 4500, salePrice: 4000, costPrice: 2800,
    stock: 100, reservedStock: 10, minStockAlert: 20, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '6 Months',
    specs: [{ key: 'length', label: 'Length', value: '50m' }],
    usage: 'Both', powerRequirements: 'N/A',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-acc-002', name: 'CAT6 UTP Network Cable 305m Box',
    brandId: 'b5', modelNumber: 'CAT6-305M', sku: 'ACC-CAT6-305M-001',
    categoryId: 'cat4', subcategoryId: 'sub30', productType: 'Network Cable',
    shortDescription: 'CAT6 UTP network cable, 305m pull box, suitable for IP cameras & networking',
    mainImage: '', galleryImages: [],
    regularPrice: 22000, salePrice: 19500, costPrice: 14000,
    stock: 30, reservedStock: 5, minStockAlert: 5, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '1 Year',
    specs: [{ key: 'length', label: 'Length', value: '305m' }],
    usage: 'Both', powerRequirements: 'N/A',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-acc-003', name: '12V 5A CCTV Power Supply Box (4-way)',
    brandId: 'b5', modelNumber: 'PSU-12V5A-4CH', sku: 'ACC-PSU-4CH-001',
    categoryId: 'cat4', subcategoryId: 'sub31', productType: 'Power Supply',
    shortDescription: '12V 5A 4-channel power supply box for analog CCTV cameras',
    mainImage: '', galleryImages: [],
    regularPrice: 3800, salePrice: 3500, costPrice: 2400,
    stock: 80, reservedStock: 10, minStockAlert: 20, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: true, status: 'active',
    warrantyPeriod: '1 Year',
    specs: [{ key: 'channels', label: 'Channels', value: '4' }],
    usage: 'Indoor', powerRequirements: 'AC220V',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  // ── Custom / Installation Services ──
  {
    id: 'cp-svc-001', name: 'CCTV Camera Installation Service (Per Camera)',
    brandId: 'b5', modelNumber: 'SVC-INST-CAM', sku: 'SVC-INST-CAM-001',
    categoryId: 'cat4', subcategoryId: 'sub37', productType: 'Installation Service',
    shortDescription: 'Professional CCTV camera installation, cable routing and configuration',
    mainImage: '', galleryImages: [],
    regularPrice: 2500, salePrice: 2500, costPrice: 1200,
    stock: 999, reservedStock: 0, minStockAlert: 0, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: false, status: 'active',
    warrantyPeriod: '1 Year K-Tec Service Warranty',
    specs: [],
    usage: 'Both',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'cp-svc-002', name: 'Network Setup & Configuration Service',
    brandId: 'b5', modelNumber: 'SVC-NET-SETUP', sku: 'SVC-NET-SETUP-001',
    categoryId: 'cat4', subcategoryId: 'sub37', productType: 'Installation Service',
    shortDescription: 'Network router/switch installation, IP configuration, remote viewing setup',
    mainImage: '', galleryImages: [],
    regularPrice: 5000, salePrice: 5000, costPrice: 2500,
    stock: 999, reservedStock: 0, minStockAlert: 0, inStock: true,
    isFeatured: false, isNewProduct: false, isBestSeller: false, status: 'active',
    warrantyPeriod: '6 Months K-Tec Service Warranty',
    specs: [],
    usage: 'Both',
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
];

// ─── Seed Packages ─────────────────────────────────────────────────────────────
const seedPackages: CCTVPackage[] = [
  {
    id: 'pkg1',
    title: '4 Camera CCTV Package',
    slug: '4-camera-package',
    description: 'Complete 4-camera surveillance solution with professional installation.',
    packageType: '4cam',
    cameraQty: 4,
    items: [],
    sellingPrice: 0,
    isEnabled: true,
    isFeatured: true,
    isMostPopular: false,
    inclusions: [
      '4 × Selected Cameras',
      '1 × 4/8 Channel DVR or NVR',
      '1 × Surveillance Hard Drive',
      'Required Power Supply or PoE Equipment',
      'Required Connectors & Cabling',
      'Professional Installation & Configuration',
      'Mobile Phone Live View Setup',
      'Remote Viewing Configuration',
      'User Training',
      'Product Warranty',
      'K-Tec Service Warranty',
    ],
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'pkg2',
    title: '8 Camera CCTV Package',
    slug: '8-camera-package',
    description: 'Comprehensive 8-camera surveillance system ideal for medium properties.',
    packageType: '8cam',
    cameraQty: 8,
    items: [],
    sellingPrice: 0,
    isEnabled: true,
    isFeatured: false,
    isMostPopular: true,
    inclusions: [
      '8 × Selected Cameras',
      '1 × Compatible DVR / NVR',
      '1 × Surveillance Hard Drive',
      'Required Accessories & Cabling',
      'Professional Installation & Configuration',
      'Mobile Phone Live View Setup',
      'Remote Viewing Configuration',
      'User Training',
      'Product Warranty',
      'K-Tec Service Warranty',
    ],
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'pkg3',
    title: '16 Camera CCTV Package',
    slug: '16-camera-package',
    description: 'Enterprise-grade 16-camera system for large properties and commercial use.',
    packageType: '16cam',
    cameraQty: 16,
    items: [],
    sellingPrice: 0,
    isEnabled: true,
    isFeatured: false,
    isMostPopular: false,
    inclusions: [
      '16 × Selected Cameras',
      '1 × 16 Channel DVR / NVR',
      '1 × Surveillance Hard Drive (4TB+)',
      'Required Networking & Accessories',
      'Professional Installation & Configuration',
      'Mobile Phone Live View Setup',
      'Remote Viewing Configuration',
      'User Training',
      'Product Warranty',
      'K-Tec Service Warranty',
    ],
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    id: 'pkg4',
    title: 'Custom CCTV Package',
    slug: 'custom-package',
    description: 'Tailored surveillance solution designed specifically for your needs.',
    packageType: 'custom',
    cameraQty: 0,
    items: [],
    sellingPrice: 0,
    isEnabled: true,
    isFeatured: false,
    isMostPopular: false,
    inclusions: [
      'Custom Number of Selected Cameras',
      'Compatible DVR / NVR',
      'Appropriate Surveillance Storage',
      'Required Accessories & Cabling',
      'Professional Installation & Configuration',
      'Full Setup & Training',
      'Product & Service Warranty',
    ],
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
];

// ─── Store Interface ──────────────────────────────────────────────────────────
interface CatalogStore {
  // State
  brands: Brand[];
  categories: Category[];
  catalogProducts: CatalogProduct[];
  packages: CCTVPackage[];

  // Brand CRUD
  addBrand: (brand: Brand) => void;
  updateBrand: (id: string, data: Partial<Brand>) => void;
  deleteBrand: (id: string) => void;
  reorderBrand: (id: string, direction: 'up' | 'down') => void;

  // Category CRUD
  addCategory: (category: Category) => void;
  updateCategory: (id: string, data: Partial<Category>) => void;
  deleteCategory: (id: string) => void;
  reorderCategory: (id: string, direction: 'up' | 'down') => void;

  // Catalog Product CRUD
  addCatalogProduct: (product: CatalogProduct) => void;
  updateCatalogProduct: (id: string, data: Partial<CatalogProduct>) => void;
  deleteCatalogProduct: (id: string) => void;
  duplicateCatalogProduct: (id: string) => void;
  updateProductImages: (productId: string, images: ProductImage[], mainImage?: string) => void;

  // Package CRUD
  addPackage: (pkg: CCTVPackage) => void;
  updatePackage: (id: string, data: Partial<CCTVPackage>) => void;
  deletePackage: (id: string) => void;
  duplicatePackage: (id: string) => void;
  addPackageItem: (packageId: string, item: PackageItem) => void;
  updatePackageItem: (packageId: string, itemId: string, data: Partial<PackageItem>) => void;
  removePackageItem: (packageId: string, itemId: string) => void;
  reorderPackageItem: (packageId: string, itemId: string, direction: 'up' | 'down') => void;
}

// ─── Helper: generate UUID ────────────────────────────────────────────────────
function genId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

// ─── Store ────────────────────────────────────────────────────────────────────
export const useCatalogStore = create<CatalogStore>()(
  persist(
    (set, get) => ({
      brands: seedBrands,
      categories: seedCategories,
      catalogProducts: seedCatalogProducts,
      packages: seedPackages,

      // ── Brand ──────────────────────────────────────────────────────────────
      addBrand: (brand) =>
        set((s) => ({ brands: [...s.brands, brand] })),

      updateBrand: (id, data) =>
        set((s) => ({
          brands: s.brands.map((b) =>
            b.id === id ? { ...b, ...data, updatedAt: new Date().toISOString() } : b
          ),
        })),

      deleteBrand: (id) =>
        set((s) => ({ brands: s.brands.filter((b) => b.id !== id) })),

      reorderBrand: (id, direction) =>
        set((s) => {
          const list = [...s.brands].sort((a, b) => a.displayOrder - b.displayOrder);
          const idx = list.findIndex((b) => b.id === id);
          if (idx === -1) return s;
          const newIdx = direction === 'up' ? idx - 1 : idx + 1;
          if (newIdx < 0 || newIdx >= list.length) return s;
          [list[idx].displayOrder, list[newIdx].displayOrder] = [list[newIdx].displayOrder, list[idx].displayOrder];
          return { brands: list };
        }),

      // ── Category ──────────────────────────────────────────────────────────
      addCategory: (cat) =>
        set((s) => ({ categories: [...s.categories, cat] })),

      updateCategory: (id, data) =>
        set((s) => ({
          categories: s.categories.map((c) =>
            c.id === id ? { ...c, ...data, updatedAt: new Date().toISOString() } : c
          ),
        })),

      deleteCategory: (id) =>
        set((s) => ({ categories: s.categories.filter((c) => c.id !== id) })),

      reorderCategory: (id, direction) =>
        set((s) => {
          const list = [...s.categories].sort((a, b) => a.displayOrder - b.displayOrder);
          const idx = list.findIndex((c) => c.id === id);
          if (idx === -1) return s;
          const newIdx = direction === 'up' ? idx - 1 : idx + 1;
          if (newIdx < 0 || newIdx >= list.length) return s;
          [list[idx].displayOrder, list[newIdx].displayOrder] = [list[newIdx].displayOrder, list[idx].displayOrder];
          return { categories: list };
        }),

      // ── Catalog Products ──────────────────────────────────────────────────
      addCatalogProduct: (product) =>
        set((s) => ({ catalogProducts: [product, ...s.catalogProducts] })),

      updateCatalogProduct: (id, data) =>
        set((s) => ({
          catalogProducts: s.catalogProducts.map((p) =>
            p.id === id ? { ...p, ...data, updatedAt: new Date().toISOString() } : p
          ),
        })),

      deleteCatalogProduct: (id) =>
        set((s) => ({ catalogProducts: s.catalogProducts.filter((p) => p.id !== id) })),

      duplicateCatalogProduct: (id) =>
        set((s) => {
          const original = s.catalogProducts.find((p) => p.id === id);
          if (!original) return s;
          const now = new Date().toISOString();
          const copy: CatalogProduct = {
            ...original,
            id: genId(),
            name: `${original.name} (Copy)`,
            sku: `${original.sku}-COPY`,
            galleryImages: [],
            mainImage: original.mainImage,
            createdAt: now,
            updatedAt: now,
          };
          return { catalogProducts: [copy, ...s.catalogProducts] };
        }),

      updateProductImages: (productId, images, mainImage) =>
        set((s) => ({
          catalogProducts: s.catalogProducts.map((p) =>
            p.id === productId
              ? {
                  ...p,
                  galleryImages: images,
                  mainImage: mainImage ?? p.mainImage,
                  updatedAt: new Date().toISOString(),
                }
              : p
          ),
        })),

      // ── Packages ──────────────────────────────────────────────────────────
      addPackage: (pkg) =>
        set((s) => ({ packages: [pkg, ...s.packages] })),

      updatePackage: (id, data) =>
        set((s) => ({
          packages: s.packages.map((pkg) =>
            pkg.id === id ? { ...pkg, ...data, updatedAt: new Date().toISOString() } : pkg
          ),
        })),

      deletePackage: (id) =>
        set((s) => ({ packages: s.packages.filter((pkg) => pkg.id !== id) })),

      duplicatePackage: (id) =>
        set((s) => {
          const original = s.packages.find((pkg) => pkg.id === id);
          if (!original) return s;
          const now = new Date().toISOString();
          const copy: CCTVPackage = {
            ...original,
            id: genId(),
            title: `${original.title} (Copy)`,
            slug: `${original.slug}-copy-${Date.now()}`,
            items: original.items.map((i) => ({ ...i, id: genId(), packageId: '' })),
            createdAt: now,
            updatedAt: now,
          };
          copy.items = copy.items.map((i) => ({ ...i, packageId: copy.id }));
          return { packages: [copy, ...s.packages] };
        }),

      addPackageItem: (packageId, item) =>
        set((s) => ({
          packages: s.packages.map((pkg) =>
            pkg.id === packageId
              ? { ...pkg, items: [...pkg.items, item], updatedAt: new Date().toISOString() }
              : pkg
          ),
        })),

      updatePackageItem: (packageId, itemId, data) =>
        set((s) => ({
          packages: s.packages.map((pkg) =>
            pkg.id === packageId
              ? {
                  ...pkg,
                  items: pkg.items.map((i) => (i.id === itemId ? { ...i, ...data } : i)),
                  updatedAt: new Date().toISOString(),
                }
              : pkg
          ),
        })),

      removePackageItem: (packageId, itemId) =>
        set((s) => ({
          packages: s.packages.map((pkg) =>
            pkg.id === packageId
              ? {
                  ...pkg,
                  items: pkg.items.filter((i) => i.id !== itemId),
                  updatedAt: new Date().toISOString(),
                }
              : pkg
          ),
        })),

      reorderPackageItem: (packageId, itemId, direction) =>
        set((s) => ({
          packages: s.packages.map((pkg) => {
            if (pkg.id !== packageId) return pkg;
            const items = [...pkg.items].sort((a, b) => a.displayOrder - b.displayOrder);
            const idx = items.findIndex((i) => i.id === itemId);
            if (idx === -1) return pkg;
            const newIdx = direction === 'up' ? idx - 1 : idx + 1;
            if (newIdx < 0 || newIdx >= items.length) return pkg;
            [items[idx].displayOrder, items[newIdx].displayOrder] = [
              items[newIdx].displayOrder,
              items[idx].displayOrder,
            ];
            return { ...pkg, items };
          }),
        })),
    }),
    {
      name: 'ktec-catalog-store',
      storage: createJSONStorage(() => localStorage),
      version: 2,
      migrate: (persistedState: any) => {
        if (!persistedState?.catalogProducts?.length) {
          return { ...persistedState, catalogProducts: seedCatalogProducts };
        }
        return persistedState;
      },
      merge: (persistedState: any, currentState: any) => ({
        ...currentState,
        ...persistedState,
        catalogProducts:
          (persistedState?.catalogProducts?.length ?? 0) > 0
            ? persistedState.catalogProducts
            : seedCatalogProducts,
      }),
    }
  )
);

export { genId, seedCatalogProducts };
